
import { useState, useEffect, useCallback, useRef } from 'react';
import { Attendee } from '@/types/attendee';
import { SortField, SortDirection } from '@/components/admin/table/SortableTableHeader';
import { useToast } from '@/hooks/use-toast';
import { useAttendanceEvents } from '@/hooks/admin/useAttendanceEvents';

export const useAttendeeTable = (initialAttendees: Attendee[]) => {
  const [sortField, setSortField] = useState<SortField>('registeredAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [localAttendees, setLocalAttendees] = useState<Attendee[]>(initialAttendees);
  const [lastUpdateTime, setLastUpdateTime] = useState<string>('');
  const { toast } = useToast();
  const { dispatchUpdateEvent } = useAttendanceEvents();
  
  // Enhanced state tracking for recently updated attendees with timestamps
  const [updatedAttendeeCodes, setUpdatedAttendeeCodes] = useState<Record<string, number>>({});
  const attendeeUpdateRef = useRef(new Map<string, Attendee>());
  const lastInitialAttendeeUpdateTime = useRef<number>(Date.now());
  const pendingManualUpdates = useRef(new Map<string, Attendee>());

  // Sync local state with props with improved state preservation
  useEffect(() => {
    const currentTime = Date.now();
    
    // Always update our reference time when initialAttendees changes significantly
    if (initialAttendees.length !== localAttendees.length) {
      lastInitialAttendeeUpdateTime.current = currentTime;
    }
    
    // Build optimized lookup maps for faster comparison
    const initialMap = new Map(initialAttendees.map(a => [a.code, a]));
    const localMap = new Map(localAttendees.map(a => [a.code, a]));
    const updatedCodes = Object.keys(updatedAttendeeCodes);
    
    // First, determine if we need to update based on initialAttendees
    let needsUpdate = false;
    let hasAttendanceChanges = false;
    
    // Check for changes that would require an update
    for (const attendee of initialAttendees) {
      const localAttendee = localMap.get(attendee.code);
      
      // If local doesn't have this attendee, or has different attendance status, we need to update
      if (!localAttendee || 
          localAttendee.attended !== attendee.attended ||
          localAttendee.checkedInAt !== attendee.checkedInAt) {
        
        needsUpdate = true;
        if (localAttendee && localAttendee.attended !== attendee.attended) {
          hasAttendanceChanges = true;
        }
        break;
      }
    }
    
    // Apply any pending manual updates first
    if (pendingManualUpdates.current.size > 0) {
      console.log(`Applying ${pendingManualUpdates.current.size} manual updates before merging with initialAttendees`);
      
      // Create a new array from initialAttendees
      const updatedAttendees = [...initialAttendees];
      
      // Apply manual updates
      pendingManualUpdates.current.forEach((manualUpdate, code) => {
        const index = updatedAttendees.findIndex(a => a.code === code);
        if (index >= 0) {
          updatedAttendees[index] = {
            ...updatedAttendees[index],
            attended: manualUpdate.attended,
            checkedInAt: manualUpdate.checkedInAt,
            t_shirt_size: manualUpdate.t_shirt_size || updatedAttendees[index].t_shirt_size
          };
          console.log(`Applied manual update for ${code}, attended: ${manualUpdate.attended}`);
        }
      });
      
      // Update local attendees with these changes
      setLocalAttendees(updatedAttendees);
      setLastUpdateTime(new Date().toLocaleTimeString('vi-VN'));
      
      // Clear the pending updates after applying
      pendingManualUpdates.current.clear();
      
      // We've handled this update
      return;
    }
    
    // If we have new data or attendance changes, merge with preservation of local updates
    if (needsUpdate || hasAttendanceChanges || 
        currentTime - lastInitialAttendeeUpdateTime.current > 10000) { // Force update after 10s
      
      lastInitialAttendeeUpdateTime.current = currentTime;
      
      // Create a result array starting with initialAttendees
      const mergedAttendees = [...initialAttendees];
      
      // Apply any pending local updates that may not be in the initialAttendees yet
      for (const [code, localAttendee] of attendeeUpdateRef.current.entries()) {
        const initialIndex = mergedAttendees.findIndex(a => a.code === code);
        
        if (initialIndex >= 0) {
          const initialAttendee = mergedAttendees[initialIndex];
          
          // Only override if local attended status is different AND it's more recent
          // Or if initial wasn't attended but local is
          if ((localAttendee.attended !== initialAttendee.attended && 
               updatedAttendeeCodes[code] > lastInitialAttendeeUpdateTime.current - 10000) || 
              (!initialAttendee.attended && localAttendee.attended)) {
            
            // Use the local version with the most up-to-date attendance status
            mergedAttendees[initialIndex] = {
              ...initialAttendee,
              attended: localAttendee.attended,
              checkedInAt: localAttendee.checkedInAt
            };
            
            console.log(`Preserving local update for ${code}: ${localAttendee.attended ? 'attended' : 'not attended'}`);
          }
        }
      }
      
      // Update the state with merged data
      setLocalAttendees(mergedAttendees);
    }
  }, [initialAttendees, localAttendees, updatedAttendeeCodes]);

  // Handle sorting - memoized to prevent rerenders
  const handleSort = useCallback((field: SortField) => {
    setSortField(prev => {
      if (prev === field) {
        // Toggle direction if same field
        setSortDirection(prevDir => prevDir === 'asc' ? 'desc' : 'asc');
        return field;
      } else {
        // Set new field and default to desc
        setSortDirection('desc');
        return field;
      }
    });
  }, []);

  // Update a single attendee locally with improved persistence
  const updateAttendeeLocally = useCallback((updatedAttendee: Attendee) => {
    if (!updatedAttendee || !updatedAttendee.code) return;
    
    const code = updatedAttendee.code;
    const updateTime = Date.now();
    
    console.log(`Updating attendee locally: ${code}, attended: ${updatedAttendee.attended}`);
    
    // Store in our Map for reference
    attendeeUpdateRef.current.set(code, updatedAttendee);
    
    // Track recently updated codes with timestamps for persistence
    setUpdatedAttendeeCodes(prev => ({
      ...prev,
      [code]: updateTime
    }));
    
    // Update the attendee in local state with immutable pattern
    setLocalAttendees(prev => 
      prev.map(a => a.code === code ? {
        ...a,
        attended: updatedAttendee.attended,
        checkedInAt: updatedAttendee.checkedInAt || a.checkedInAt,
        t_shirt_size: updatedAttendee.t_shirt_size || a.t_shirt_size
      } : a)
    );
    
    // Update last update time for UI feedback
    const now = new Date().toLocaleTimeString('vi-VN');
    setLastUpdateTime(now);
  }, []);

  // Handle check-in success with immediate UI update and persistence
  const handleCheckInSuccess = useCallback((updatedAttendee: Attendee, timestamp: string) => {
    // Track this update to ensure it persists
    const updateTime = Date.now();
    const code = updatedAttendee.code;
    
    // Store in our reference Map
    attendeeUpdateRef.current.set(code, updatedAttendee);
    
    setUpdatedAttendeeCodes(prev => ({
      ...prev,
      [code]: updateTime
    }));
    
    // Immediate update to local state for instant feedback
    updateAttendeeLocally(updatedAttendee);
    
    // Update last update time for UI feedback
    const now = new Date().toLocaleTimeString('vi-VN');
    setLastUpdateTime(now);
    
    // Dispatch event for other components to react with high priority
    dispatchUpdateEvent(updatedAttendee, timestamp);
    
    // Show success feedback with non-blocking toast
    toast({
      title: "Cập nhật danh sách thành công",
      description: `Đã cập nhật trạng thái cho ${updatedAttendee.name}.`,
    });
  }, [toast, updateAttendeeLocally, dispatchUpdateEvent]);

  // Handle manual code entry specifically
  const handleManualCodeUpdate = useCallback((attendee: Attendee) => {
    if (!attendee || !attendee.code) return;
    
    console.log(`Handling specific manual code update for ${attendee.code}`);
    
    // Store this in our manual updates map for priority processing
    pendingManualUpdates.current.set(attendee.code, attendee);
    
    // Force an immediate update to local state
    setLocalAttendees(prev => {
      const index = prev.findIndex(a => a.code === attendee.code);
      if (index === -1) return prev; // Attendee not found, return unchanged
      
      const newAttendees = [...prev];
      newAttendees[index] = {
        ...newAttendees[index],
        attended: attendee.attended,
        checkedInAt: attendee.checkedInAt,
        t_shirt_size: attendee.t_shirt_size || newAttendees[index].t_shirt_size
      };
      
      return newAttendees;
    });
    
    // Update the timestamp
    const now = new Date().toLocaleTimeString('vi-VN');
    setLastUpdateTime(now);
  }, []);

  // Enhanced real-time update event handling with special handling for manual entries
  useEffect(() => {
    const handleQrScan = (event: CustomEvent) => {
      // Get attendee from various event formats
      const attendee = event.detail?.attendee || event.detail?.updatedAttendee;
      const wasManualEntry = event.detail?.wasManualEntry === true || 
                            event.detail?.source === 'manual-code-entry';
      
      if (attendee && attendee.code) {
        console.log(`useAttendeeTable: Received ${event.type} event for ${attendee.code}`, 
                    wasManualEntry ? '(manual entry)' : '');
        console.log(`Attendance status: ${attendee.attended}, checked in at: ${attendee.checkedInAt}`);
        
        // Use different handling for manual entries vs QR scans
        if (wasManualEntry) {
          // Manual entries get priority handling
          handleManualCodeUpdate(attendee);
        } else {
          // Standard handling for other update types
          attendeeUpdateRef.current.set(attendee.code, attendee);
          
          // Track this update to ensure it persists
          setUpdatedAttendeeCodes(prev => ({
            ...prev,
            [attendee.code]: Date.now()
          }));
          
          updateAttendeeLocally(attendee);
        }
      }
    };

    // Listen to multiple event types to ensure we catch all updates
    window.addEventListener('qr-scan-confirmed', handleQrScan as EventListener);
    window.addEventListener('database-update-success', handleQrScan as EventListener);
    window.addEventListener('database-direct-update', handleQrScan as EventListener);
    window.addEventListener('attendance-direct-confirmed', handleQrScan as EventListener);
    window.addEventListener('attendance-confirmed', handleQrScan as EventListener);
    window.addEventListener('attendance-ui-update', handleQrScan as EventListener);
    window.addEventListener('manual-code-processed', handleQrScan as EventListener);
    
    return () => {
      // Clean up all event listeners
      window.removeEventListener('qr-scan-confirmed', handleQrScan as EventListener);
      window.removeEventListener('database-update-success', handleQrScan as EventListener);
      window.removeEventListener('database-direct-update', handleQrScan as EventListener);
      window.removeEventListener('attendance-direct-confirmed', handleQrScan as EventListener);
      window.removeEventListener('attendance-confirmed', handleQrScan as EventListener);
      window.removeEventListener('attendance-ui-update', handleQrScan as EventListener);
      window.removeEventListener('manual-code-processed', handleQrScan as EventListener);
    };
  }, [updateAttendeeLocally, handleManualCodeUpdate]);

  return {
    sortField,
    sortDirection,
    localAttendees,
    lastUpdateTime,
    handleSort,
    handleCheckInSuccess,
    updateAttendeeLocally,
    handleManualCodeUpdate
  };
};
